import { CanonicalSegmentName } from '@/helpers/SegmentNameMapping';
import { JOINTS_OPTIONS } from '@/enums/global/GlobalOptions';
import { CombinationsChecklistFieldConfig } from '@/modules/ConfiguratorCombinations/helpers/CombinationsConfiguration';
import { JointShape } from '@/modules/ConfiguratorJoints/composables/useShapeAndAngle';
import { CornerType, GuideType, IndividualGuideTypes } from '@/modules/ConfiguratorJoints/composables/useGuidesAndType';
import { heroJointCanvasTemplate } from '@/modules/ConfiguratorJoints/helpers/HeroTemplateConfig';
import { heroTwoJointCanvasTemplate } from '@/modules/ConfiguratorJoints/helpers/HeroTwoTemplateConfig';
import { heroThreeJointCanvasTemplate } from '@/modules/ConfiguratorJoints/helpers/HeroThreeTemplateConfig';
import { heroFiveJointCanvasTemplate } from '@/modules/ConfiguratorJoints/helpers/HeroFiveTemplateConfig';
import { olimpJointCanvasTemplate } from '@/modules/ConfiguratorJoints/helpers/OlimpTemplateConfig';
import { JointCanvasTemplateConfiguration } from '@/modules/ConfiguratorJoints/types/canvasTemplate';

export interface JointsParameterRow {
    label: string;
    value: string;
    values?: JointsParameterRowValue[];
}

export interface JointsParameterRowValue {
    label: string;
    value: string;
}

interface JointsParameterValues {
    selectedShape: JointShape | null;
    angle: number;
    guideTypes: IndividualGuideTypes;
    cornerType: CornerType;
    longerArm: '1' | '2' | '';
}

const shapeLabels: Record<JointShape, string> = {
    external: 'External corner',
    internal: 'Internal corner',
};

const guideTypeLabels: Record<GuideType, string> = {
    'wire-guide': 'Wire guides',
    'inox-guide': 'Inox guides',
    'alu-guide': 'Alu guides',
    'no-guide': 'No guides',
    'concealed-guide': 'Concealed guides',
};

const cornerTypeLabels: Record<CornerType, string> = {
    'type-1': 'Type 1',
    'type-2': 'Type 2',
};

export function getJointsParameterRows(values: JointsParameterValues): JointsParameterRow[] {
    const parameterRows = [
        {
            label: 'Corner shape',
            value: values.selectedShape ? shapeLabels[values.selectedShape] : '-',
        },
        {
            label: 'Angle',
            value: `${values.angle}°`,
        },
        {
            label: 'Corner type',
            value: cornerTypeLabels[values.cornerType],
        },
        ...getIndividualGuideTypeRows(values.guideTypes),
    ];

    if (values.cornerType === 'type-1') {
        const longerPositionField = {
            label: 'Longer position',
            value: values.longerArm ?? '-',
        };

        parameterRows.splice(3, 0, longerPositionField);
    }

    return parameterRows;
}

function getIndividualGuideTypeRows(guideTypes: IndividualGuideTypes): JointsParameterRow[] {
    return ([1, 2] as const).map((position) => ({
        label: `Position ${position}`,
        value: '',
        values: [
            {
                label: 'Left side',
                value: getGuideTypeLabel(guideTypes[position].leftCorner),
            },
            {
                label: 'Right side',
                value: getGuideTypeLabel(guideTypes[position].rightCorner),
            },
        ],
    }));
}

function getGuideTypeLabel(guideType: GuideType | null): string {
    return guideType ? guideTypeLabels[guideType] : '-';
}

// =====================================================================
// Selection rules
// =====================================================================

/** A corner joint always connects exactly two products. */
export const MAX_SELECTED_POSITIONS_FOR_JOINT = 2;

// =====================================================================
// Table header fields (reuses the Combinations field-config shape)
// ---------------------------------------------------------------------
// The joints table shows the same read-only product info as the
// combinations table plus the editable assembly position dropdown that
// drives step 2 (order).
// =====================================================================

// =====================================================================
// Configurator step descriptions
// ---------------------------------------------------------------------
// One entry per implemented wizard step (1-indexed via array order).
// Step nav slices to the number of currently implemented steps.
// =====================================================================

export interface JointsConfiguratorStep {
    name: string;
    description: string;
}

export const jointsConfiguratorSteps: JointsConfiguratorStep[] = [
    {
        name: 'Selection',
        description:
            'Select the two positions you want to connect with a corner joint. In this configurator, you can connect at most two positions.',
    },
    {
        name: 'Sequence',
        description: 'Choose the order of positions (from left to right).',
    },
    {
        name: 'Shape and angle',
        description: '',
    },
    {
        name: 'Guides',
        description: '',
    },
    {
        name: 'Additional options',
        description:
            'Choose additional options for the corner joint. You can select one or both options — all selected options will be included in the order.',
    },
    {
        name: 'Preview',
        description: 'Review your selected configuration and confirm your choice.',
    },
];

export const additionalDescriptions: Record<string, string> = {
    templatePreview:
        'The preview at this stage is approximate. It will be more accurate after completing the following steps.',
    angle: 'Set the angle of the corner joint. Keep in mind that the angle is measured between the two boxes, not between the walls. You can enter the value manually or adjust it with the slider. Manual entry allows one decimal place (for example, 58.2°). If you enter more decimal places, the value will be rounded automatically to one decimal place.',
    shape: 'Choose the shape of the corner joint: external (outside corner) or internal (inside corner).',
    cornerType:
        'Choose the type of corner joint. For Type 1, you will define which position will be longer and which shorter. Type 1 is available only when the angle is exactly 90°.',
    guide: 'Choose the guide type for the product.',
};

// =====================================================================
// Canvas template configurations (per joint shape)
// ---------------------------------------------------------------------
// Each shape provides:
//   - stage dimensions + pivot (where the two halves meet)
//   - the two SVG halves (url + intrinsic mm dimensions)
//   - render scale (mm -> px)
//
// Adding a new shape later (e.g. mitred / curved) is a single new entry
// in `heroJointCanvasTemplate`; the template component does not need to
// change.
// =====================================================================

// =====================================================================
// Per-product configuration registry
// ---------------------------------------------------------------------
// Mirrors the combinations pattern: one entry per supported product
// (joined into the same entry when they share the exact same setup).
// The popup looks up `currentConfig` from this list by `productName`,
// then forwards `checklistFields` to the table and
// `canvasTemplateConfigurations` to the template component.
//
// For now every supported joint product (Hero 1 / Hero 2 / Hero) uses
// the same checklist + canvas setup, but the structure is in place so a
// new variant only needs a new entry here.
// =====================================================================

export type JointsConfiguratorType = 'hero-1' | 'hero-2' | 'hero-3' | 'hero-5' | 'olimp';

export type JointsChecklistFields = Array<CombinationsChecklistFieldConfig>;

export interface JointsConfigurationItem {
    products: string[];
    segment: CanonicalSegmentName;
    configuratorType: JointsConfiguratorType;
    configuratorSteps: number;
    maxSelectedPositions: number;
    normalizeCombinationsSizeType: 'selectedSize' | 'depthAndHeight' | 'boxSize';
    checklistFields: JointsChecklistFields;
    canvasTemplateConfigurations: Record<JointShape, JointCanvasTemplateConfiguration>;
    availableOptions: string[];
}

export const defaultCanvasTemplateConfigurations = heroJointCanvasTemplate;

const baseFields: JointsChecklistFields = [
    {
        pid: 'productId',
        label: 'Num.',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
        isNarrow: true,
    },
    {
        pid: JOINTS_OPTIONS.SISTEM_PID,
        label: 'System',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
    },
    {
        pid: JOINTS_OPTIONS.QUANTITY_PID,
        label: 'Quantity',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
        isNarrow: true,
    },
    {
        pid: JOINTS_OPTIONS.WIDTH_PID,
        label: 'Width',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
        key: 'width',
    },
    {
        pid: JOINTS_OPTIONS.HEIGHT_PID,
        label: 'Height',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
        key: 'height',
    },
    {
        pid: JOINTS_OPTIONS.POSITION_PID,
        label: 'Position',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: false,
        isReadOnly: true,
        segment: 'table',
        isNarrow: true,
    },
    {
        pid: JOINTS_OPTIONS.JOINTS_POSITION_PID,
        label: 'Corner joint position',
        type: 'dropdown',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'table',
        options: [
            { label: '1', value: '1' },
            { label: '2', value: '2' },
        ],
        step: 2,
    },
];
const heroBaseFields: JointsChecklistFields = [
    {
        pid: JOINTS_OPTIONS.JOINTS_SWITCH_PID,
        label: 'Active',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'jointsSwitch',
    },
    {
        pid: JOINTS_OPTIONS.HERO_BOTTOM_CORNER_COVER_PID,
        label: 'Bottom Corner Cover',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'options',
        key: 'bottomCornerCover',
    },
    {
        pid: JOINTS_OPTIONS.HERO_CLOSING_PLATE_PID,
        label: 'Closing Plate',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'options',
        key: 'closingPlate',
    },
    {
        pid: JOINTS_OPTIONS.HERO_LEFT_GUIDE_PID,
        label: 'Left Guide',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'guides',
        key: 'leftGuide',
    },
    {
        pid: JOINTS_OPTIONS.HERO_RIGHT_GUIDE_PID,
        label: 'Right Guide',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'guides',
        key: 'rightGuide',
    },
    {
        pid: JOINTS_OPTIONS.HERO_NO_GUIDES_SWITCH_PID,
        label: 'No Guides',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'guides',
        key: 'noGuides',
    },
];

const heroOneFields: JointsChecklistFields = [
    ...baseFields,
    ...heroBaseFields,
    {
        pid: JOINTS_OPTIONS.HERO_GUIDE_TYPE_PID,
        label: 'Guide Type',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'guides',
        key: 'guideType',
        defaultValue: '78x23 mm',
        options: [
            { label: 'wire-guide', value: 'Žično vodilo' },
            { label: 'inox-guide', value: 'Palično vodilo' },
            { label: 'alu-guide', value: '78x23 mm' },
            { label: 'no-guide', value: '' },
        ],
    },
    {
        pid: JOINTS_OPTIONS.CONSOLE_SIZE_PID,
        label: 'Roller Shutter Box Size',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'boxSize',
    },
    {
        pid: JOINTS_OPTIONS.SELECTED_CONSOLE_SIZE_PID,
        label: 'Selected Roller Shutter Box Size',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'selectedBoxSize',
    },
    {
        pid: JOINTS_OPTIONS.CONSOLE_TYPE_PID,
        label: 'Box Type',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'boxType',
    },
];

const heroTwoFields: JointsChecklistFields = [
    ...baseFields,
    {
        pid: JOINTS_OPTIONS.ROLLER_SHUTTER_BOX_SIZE_PID,
        label: 'Roller Shutter Box Size',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'boxSize',
    },
    {
        pid: JOINTS_OPTIONS.SELECTED_ROLLER_SHUTTER_BOX_SIZE_PID,
        label: 'Selected Roller Shutter Box Size',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'selectedBoxSize',
    },
    {
        pid: JOINTS_OPTIONS.CONSOLE_TYPE_PID,
        label: 'Box Type',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'boxType',
    },
];

const heroThreeFields: JointsChecklistFields = [
    ...baseFields,
    ...heroBaseFields,
    {
        pid: JOINTS_OPTIONS.HERO_GUIDE_TYPE_PID,
        label: 'Guide Type',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'guides',
        key: 'guideType',
        defaultValue: '93x23 mm',
        options: [
            { label: 'wire-guide', value: 'Žično vodilo' },
            { label: 'inox-guide', value: 'Palično vodilo' },
            { label: 'alu-guide', value: '93x23 mm' },
            { label: 'no-guide', value: '' },
            { label: 'concealed-guide', value: 'HERO.3' },
        ],
    },
    {
        pid: JOINTS_OPTIONS.CONSOLE_SIZE_PID,
        label: 'Roller Shutter Box Size',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'boxSize',
    },
    {
        pid: JOINTS_OPTIONS.SELECTED_CONSOLE_SIZE_PID,
        label: 'Selected Roller Shutter Box Size',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'selectedBoxSize',
    },
    {
        pid: JOINTS_OPTIONS.CONSOLE_TYPE_PID,
        label: 'Box Type',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'boxType',
    },
];
const heroFiveFields: JointsChecklistFields = [
    ...baseFields,
    ...heroBaseFields,
    {
        pid: JOINTS_OPTIONS.HERO_GUIDE_TYPE_PID,
        label: 'Guide Type',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'guides',
        key: 'guideType',
        defaultValue: '78x23 mm',
        options: [
            { label: 'wire-guide', value: 'Žično vodilo' },
            { label: 'inox-guide', value: 'Palično vodilo' },
            { label: 'alu-guide', value: '78x23 mm' },
            { label: 'no-guide', value: '' },
        ],
    },
    {
        pid: JOINTS_OPTIONS.ROLLER_SHUTTER_BOX_DEPTH_PID,
        label: 'Roller Shutter Box Size',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'boxSize',
    },
    {
        pid: JOINTS_OPTIONS.ROLLER_SHUTTER_BOX_HEIGHT_PID,
        label: 'Selected Roller Shutter Box Size',
        type: 'input',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'selectedBoxSize',
    },
];

const olimpFields: JointsChecklistFields = [
    ...baseFields,
    {
        pid: JOINTS_OPTIONS.HERO_BOTTOM_CORNER_COVER_PID,
        label: 'Bottom Corner Cover',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'options',
        key: 'bottomCornerCover',
    },
    {
        pid: JOINTS_OPTIONS.HERO_CLOSING_PLATE_PID,
        label: 'Closing Plate',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'options',
        key: 'closingPlate',
    },
    {
        pid: JOINTS_OPTIONS.HERO_LEFT_GUIDE_PID,
        label: 'Left Guide',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'guides',
        key: 'leftGuide',
    },
    {
        pid: JOINTS_OPTIONS.HERO_RIGHT_GUIDE_PID,
        label: 'Right Guide',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'guides',
        key: 'rightGuide',
    },
    {
        pid: JOINTS_OPTIONS.HERO_NO_GUIDES_SWITCH_PID,
        label: 'No Guides',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'guides',
        key: 'noGuides',
    },
    {
        pid: JOINTS_OPTIONS.OLIMP_JOINTS_SWITCH_PID,
        label: 'Active',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'jointsSwitch',
    },
    {
        pid: JOINTS_OPTIONS.OLIMP_GUIDE_TYPE_PID,
        label: 'Guide Type',
        type: 'input',
        active: true,
        shouldUpdateConfigurator: true,
        isReadOnly: false,
        segment: 'guides',
        key: 'guideType',
        defaultValue: 'Vidna 35x78 mm',
        options: [
            { label: 'wire-guide', value: 'Žično vodilo' },
            { label: 'inox-guide', value: 'Palično vodilo' },
            { label: 'alu-guide', value: 'Vidna 35x78 mm' },
            { label: 'no-guide', value: '' },
        ],
    },
    {
        pid: JOINTS_OPTIONS.CONSOLE_SIZE_PID,
        label: 'Roller Shutter Box Size',
        type: 'dropdown',
        active: false,
        shouldUpdateConfigurator: true,
        isReadOnly: true,
        segment: '',
        key: 'boxSize',
    },
];

export const jointsConfiguration: JointsConfigurationItem[] = [
    {
        products: ['HERO.1'],
        segment: CanonicalSegmentName.VODILA,
        configuratorType: 'hero-1',
        configuratorSteps: jointsConfiguratorSteps.length,
        maxSelectedPositions: MAX_SELECTED_POSITIONS_FOR_JOINT,
        normalizeCombinationsSizeType: 'selectedSize',
        checklistFields: heroOneFields,
        canvasTemplateConfigurations: heroJointCanvasTemplate,
        availableOptions: ['bottomCornerCover', 'closingPlate'],
    },
    // {
    //     products: ['HERO.2'],
    //     segment: CanonicalSegmentName.VODILA,
    //     configuratorType: 'hero-2',
    //     configuratorSteps: jointsConfiguratorSteps.length,
    //     maxSelectedPositions: MAX_SELECTED_POSITIONS_FOR_JOINT,
    //     normalizeCombinationsSizeType: 'selectedSize',
    //     checklistFields: heroTwoFields,
    //     canvasTemplateConfigurations: heroTwoJointCanvasTemplate,
    // },
    {
        products: ['HERO.3'],
        segment: CanonicalSegmentName.VODILA,
        configuratorType: 'hero-3',
        configuratorSteps: jointsConfiguratorSteps.length,
        checklistFields: heroThreeFields,
        maxSelectedPositions: MAX_SELECTED_POSITIONS_FOR_JOINT,
        normalizeCombinationsSizeType: 'selectedSize',
        canvasTemplateConfigurations: heroThreeJointCanvasTemplate,
        availableOptions: ['bottomCornerCover', 'closingPlate'],
    },
    {
        products: ['HERO.5', 'HERO.5X'],
        segment: CanonicalSegmentName.VODILA,
        configuratorType: 'hero-5',
        configuratorSteps: jointsConfiguratorSteps.length,
        checklistFields: heroFiveFields,
        maxSelectedPositions: MAX_SELECTED_POSITIONS_FOR_JOINT,
        normalizeCombinationsSizeType: 'depthAndHeight',
        canvasTemplateConfigurations: heroFiveJointCanvasTemplate,
        availableOptions: ['bottomCornerCover', 'closingPlate'],
    },
    // {
    //     products: ['OLIMP', 'OLIMP.X'],
    //     segment: CanonicalSegmentName.VODILA,
    //     configuratorType: 'olimp',
    //     configuratorSteps: jointsConfiguratorSteps.length,
    //     checklistFields: olimpFields,
    //     maxSelectedPositions: MAX_SELECTED_POSITIONS_FOR_JOINT,
    //     normalizeCombinationsSizeType: 'boxSize',
    //     canvasTemplateConfigurations: olimpJointCanvasTemplate,
    //     availableOptions: ['olimpCornerElement', 'closingPlate'],
    // },
];
